import React, { useEffect, useLayoutEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Box, Tab, Tabs, Typography } from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../store/store";
import { resellingAccountStatusAction } from "../../redux/actions";
import ResellingOverview from "./tabs/ResellingOverview";
import ResellingPricing from "./tabs/ResellingPricing";
import ResellingPayments from "./tabs/ResellingPayments";

const typographyStyle = {
  fontFamily: "var(--font-family-manrope)",
  fontWeight: 400,
  fontSize: "16px",
};

const tabItemSX = {
  fontFamily: "var(--font-family-manrope)",
  fontSize: 15,
  fontWeight: 500,
  color: "#374151",
  transition: "color 0.25s ease",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  py: 0.6,
  textTransform: "capitalize",
};

const Reselling: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const [value, setValue] = useState<number>(0);
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tabVal = searchParams.get("tab");

  const { accountStatusData, accountStatusLoading } = useSelector(
    (state: RootState) => state.reselling,
  );

  useEffect(() => {
    dispatch(resellingAccountStatusAction());
  }, [dispatch]);

  const isStripeConnected =
    Boolean(accountStatusData?.stripe_account_id) &&
    accountStatusData?.onboarding_status === "active";

  useLayoutEffect(() => {
    if (typeof location?.state === "number") {
      setValue(location.state);
    } else if (location?.state?.tabIndex !== undefined) {
      setValue(location.state.tabIndex);
    }
  }, [location.state]);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
    navigate(location.pathname, {
      state: { ...location.state, tabIndex: newValue },
    });
  };

  useEffect(() => {
    if (location?.state?.tabIndex !== undefined) {
      setValue(location.state.tabIndex);
    }

    if (tabVal) {
      setValue(parseInt(tabVal));
    }
  }, [location?.state, tabVal]);

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "100vh",
        px: { xs: 1.2, sm: 2.5, md: 5 },
        pt: 5,
      }}
    >
      {/* Title container */}
      <Box sx={{ mb: 2 }}>
        <Typography
          sx={{
            ...typographyStyle,
            fontFamily: "var(--font-family-rubik)",
            fontSize: "32px",
            fontWeight: 600,
            color: "#000000",
            letterSpacing: "0.00938em",
          }}
        >
          Reselling
        </Typography>

        <Typography color="textSecondary" sx={{ ...typographyStyle, mt: 0.5 }}>
          Resell Map Check-Ins and rebill AI credits to your clients through
          your own Stripe. Set your prices, keep the spread.
        </Typography>
      </Box>

      {/* Tabs */}
      <Tabs
        value={value}
        onChange={handleChange}
        variant="scrollable"
        scrollButtons={false}
        aria-label="reselling tabs"
        sx={{ borderBottom: 1, borderColor: "divider" }}
      >
        <Tab label="Overview" sx={tabItemSX} />
        <Tab label="Pricing" sx={tabItemSX} />
        <Tab label="Stripe" sx={tabItemSX} />
      </Tabs>

      {/* Alert Banner for Overview (0) and Pricing (1) tabs when Stripe is not connected */}
      {value !== 2 && !accountStatusLoading && !isStripeConnected && (
        <Box
          sx={{
            width: "fit-content",
            maxWidth: "100%",
            backgroundColor: "#FFF9E6",
            border: "1px solid #FFE49E",
            borderRadius: "10px",
            py: 1.2,
            px: 2.2,
            mt: 3,
            mb: -1,
            display: "flex",
            alignItems: "center",
            gap: 1.2,
            boxSizing: "border-box",
          }}
        >
          <ErrorOutlineIcon
            sx={{ color: "#D97706", fontSize: "20px", flexShrink: 0 }}
          />
          <Typography
            sx={{
              fontSize: "14px",
              fontFamily: "var(--font-family-manrope)",
              color: "#B45309",
              lineHeight: 1.5,
            }}
          >
            <span style={{ fontWeight: 700, color: "#92400E" }}>
              Connect Stripe to start billing.{" "}
            </span>
            Your prices are saved but clients won't be charged until Stripe is
            connected.{" "}
            <Typography
              component="span"
              onClick={() => {
                setValue(2);
                navigate(location.pathname, {
                  state: { ...location.state, tabIndex: 2 },
                });
              }}
              sx={{
                color: "#1A73E8",
                fontSize: "14px",
                fontWeight: 500,
                fontFamily: "var(--font-family-manrope)",
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                ml: 0.5,
                "&:hover": { textDecoration: "underline" },
              }}
            >
              Go to Stripe Integration
              <ArrowForwardIcon sx={{ fontSize: "15px" }} />
            </Typography>
          </Typography>
        </Box>
      )}

      <Box
        sx={{
          mt: 3.5,
          mx: 0,
        }}
      >
        {value === 0 && <ResellingOverview />}
        {value === 1 && <ResellingPricing />}
        {value === 2 && <ResellingPayments />}
      </Box>
    </Box>
  );
};

export default Reselling;
